import React, { useState } from "react";
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationCircle,
  FaPaperPlane,
  FaPhoneAlt,
} from "react-icons/fa";
import Header from "./Header";
import Footer from "./Footer";
import "./ContactUs.css";

const SUBJECT_MAX = 80;
const MESSAGE_MAX = 1000;

const CONTACT_EMAIL1 = "goourhub@gmail.com";
const CONTACT_EMAIL2 = "contact@goourhub.com";
const CONTACT_PHONE = "210-954-1284";
const CONTACT_PHONE_LINK = "+12109541284";

/*
  The form posts to send.php, which sits next to index.html in public_html on the GoDaddy
  cPanel host. It logs every message on the server and emails it to contact@goourhub.com
  and goourhub@gmail.com. The URL is absolute so the same build works on goourhub.com, inside
  the Capacitor iOS/Android app, and in local dev. Override with REACT_APP_CONTACT_ENDPOINT
  in .env if needed.

  Note the "www": the host 301-redirects goourhub.com → www.goourhub.com, and a redirect
  turns the POST into a GET, which send.php rejects. Always use the canonical www URL.

  The body is sent as a classic form post (application/x-www-form-urlencoded) rather than
  JSON: PHP reads it straight from $_POST, and browsers treat it as a "simple" request, so
  the app never needs a CORS preflight to succeed on shared hosting.
*/
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT || "https://www.goourhub.com/send.php";

const REQUEST_TIMEOUT_MS = 20000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FALLBACK_ERROR = `Something went wrong while sending your message. Please email us directly at ${CONTACT_EMAIL1}.`;

// Honeypot field: invisible to people, but bots tend to fill every input they find.
// The server silently drops any submission where this has a value.
const honeypotStyle = {
  position: "absolute",
  left: "-10000px",
  top: "auto",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  opacity: 0,
};

function ContactUs() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "idle", text: "" });

  const subjectCharsLeft = SUBJECT_MAX - subject.length;
  const messageCharsLeft = MESSAGE_MAX - message.length;
  const helperTone = messageCharsLeft < 120 ? "is-warning" : "";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setStatus({
        type: "error",
        text: "Please fill in all required fields before sending.",
      });
      return;
    }

    if (!EMAIL_PATTERN.test(email.trim())) {
      setStatus({
        type: "error",
        text: "Please enter a valid email address so we can reply to you.",
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: "idle", text: "" });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        body: new URLSearchParams({
          form: "contact",
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          website,
        }),
        signal: controller.signal,
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null; // e.g. an HTML error page from the host instead of JSON
      }

      if (!response.ok || !data || !data.ok) {
        throw new Error((data && data.error) || FALLBACK_ERROR);
      }

      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setWebsite("");

      setStatus({
        type: "success",
        text: "Your message has been sent. We’ll get back to you soon.",
      });
    } catch (error) {
      console.error("Contact form error:", error);

      let text = error?.message || FALLBACK_ERROR;
      if (error?.name === "AbortError") {
        text = `The request timed out. Please try again or email us directly at ${CONTACT_EMAIL1}.`;
      } else if (error instanceof TypeError) {
        // fetch() throws TypeError when the server can't be reached at all
        text = `We couldn’t reach the server. Check your connection and try again, or email us directly at ${CONTACT_EMAIL1}.`;
      }

      setStatus({ type: "error", text });
    } finally {
      clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page-shell">
      <Header />

      <main className="contact-page">
        <section className="contact-hero-card">
          <div className="contact-hero-copy">
            <span className="contact-kicker">Contact GoOurHub</span>
            <h1>Let’s talk about support, partnerships, or your next move.</h1>
            <p>
              Whether you need help with the Hub, HubMart, business visibility,
              or a partnership conversation, send a clear message and we’ll take
              it from there.
            </p>

            <div className="contact-direct-card">
              <div className="contact-direct-item">
                <span className="contact-direct-item__icon">
                  <FaEnvelope />
                </span>

                <div>
                  <span className="contact-direct-label">Email us</span>
                  <div className="contact-direct-links">
                    <a className="contact-direct-link" href={`mailto:${CONTACT_EMAIL1}`}>
                      {CONTACT_EMAIL1}
                    </a>
                    <a className="contact-direct-link" href={`mailto:${CONTACT_EMAIL2}`}>
                      {CONTACT_EMAIL2}
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-direct-item">
                <span className="contact-direct-item__icon">
                  <FaPhoneAlt />
                </span>

                <div>
                  <span className="contact-direct-label">Call us</span>
                  <a className="contact-direct-link" href={`tel:${CONTACT_PHONE_LINK}`}>
                    {CONTACT_PHONE}
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-points">
              <div className="contact-point">
                <span className="contact-point__icon">
                  <FaEnvelope />
                </span>
                <div>
                  <strong>Direct contact</strong>
                  <p>
                    Use the form below for questions, support, or business
                    inquiries.
                  </p>
                </div>
              </div>

              <div className="contact-point">
                <span className="contact-point__icon">
                  <FaPaperPlane />
                </span>
                <div>
                  <strong>Clearer messages get faster replies</strong>
                  <p>
                    Add the reason for reaching out, what page you were on, and
                    anything relevant.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-card">
            <div className="contact-card__header">
              <div>
                <span className="contact-card__eyebrow">Send a message</span>
                <h2>We’re listening</h2>
              </div>
              <div className="contact-card__meta">Direct inbox delivery</div>
            </div>

            {status.type !== "idle" && (
              <div
                className={`contact-status contact-status--${status.type}`}
                role="status"
              >
                <span className="contact-status__icon">
                  {status.type === "success" ? (
                    <FaCheckCircle />
                  ) : (
                    <FaExclamationCircle />
                  )}
                </span>
                <span>{status.text}</span>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="contact-grid">
                <div className="contact-field">
                  <label htmlFor="contact-name">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="contact-field">
                <div className="contact-field__topline">
                  <label htmlFor="contact-subject">Subject</label>
                  <span className="contact-counter">{subjectCharsLeft} left</span>
                </div>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  placeholder="What do you need help with?"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={SUBJECT_MAX}
                  required
                />
              </div>

              <div className="contact-field">
                <div className="contact-field__topline">
                  <label htmlFor="contact-message">Message</label>
                  <span className={`contact-counter ${helperTone}`}>
                    {messageCharsLeft} left
                  </span>
                </div>
                <textarea
                  id="contact-message"
                  name="message"
                  placeholder="Tell us what’s going on, what page you were on, and what outcome you need."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={MESSAGE_MAX}
                  required
                  rows={7}
                />
              </div>

              {/* Honeypot — keep this out of the visible layout; do not remove */}
              <div style={honeypotStyle} aria-hidden="true">
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className="contact-submit-btn"
                disabled={isSubmitting}
              >
                <FaPaperPlane />
                <span>{isSubmitting ? "Sending..." : "Send message"}</span>
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ContactUs;
